import { useEffect, useState } from "react";
import { storage } from "../../state/storage.js";
import { useT } from "../../i18n/use-t.js";
import type {
  Chat,
  Lorebook,
  Persona,
  Preset,
  ProviderProfile,
} from "@roqeet/core";
import { providerSupportsCapability } from "@roqeet/core";
import { useEscapeToClose } from "../use-escape-to-close.js";

/**
 * 대화별 설정(결정 #12/#22). 한 대화에만 적용되는 설정을 편집한다:
 *  - 제목
 *  - 페르소나(사용자 좌석, 결정 #22)
 *  - 프리셋 override(없으면 전역 기본, 결정 #12)
 *  - 프로바이더 override(없으면 전역 기본, 결정 #7)
 *  - 작가 노트 + 주입 깊이(결정 #12)
 *  - 채팅 레벨 로어북(캐릭터 임베드에 더해, 결정 #12)
 *
 * 저장은 storage 포트(결정 #20). 닫으면 onSaved로 부모에 알려 헤더 등을 갱신.
 */
export function ChatSettingsPanel({
  chat,
  onClose,
  onSaved,
}: {
  chat: Chat;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const { t } = useT();
  const [title, setTitle] = useState(chat.title);
  const [personaId, setPersonaId] = useState(
    chat.participants.find((p) => p.type === "user")?.personaId,
  );
  const [presetId, setPresetId] = useState(chat.presetId);
  const [providerId, setProviderId] = useState<string | undefined>(undefined);
  const [authorNote, setAuthorNote] = useState(chat.authorNote ?? "");
  const [authorNoteDepth, setAuthorNoteDepth] = useState(chat.authorNoteDepth ?? 4);
  const [lorebookIds, setLorebookIds] = useState<string[]>(chat.lorebookIds);

  const [personas, setPersonas] = useState<Persona[]>([]);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [providers, setProviders] = useState<ProviderProfile[]>([]);
  const [lorebooks, setLorebooks] = useState<Lorebook[]>([]);
  const [busy, setBusy] = useState(false);
  useEscapeToClose(onClose, !busy);

  useEffect(() => {
    void (async () => {
      const [ps, pr, pv, lb] = await Promise.all([
        storage.personas.list(),
        storage.presets.list(),
        storage.providerProfiles.list(),
        storage.lorebooks.list(),
      ]);
      setPersonas(ps);
      setPresets(pr);
      setProviders(pv.filter((p) => p.enabled && providerSupportsCapability(p, "chat")));
      setLorebooks(lb);
    })();
    // 대화 extensions에 저장된 프로바이더 override를 읽는다.
    const ext = chat.extensions as { providerProfileId?: string } | undefined;
    setProviderId(ext?.providerProfileId);
  }, [chat]);

  const save = async () => {
    setBusy(true);
    try {
      // 사용자 좌석의 personaId 갱신(결정 #22). 없으면 좌석 추가.
      const participants = [...chat.participants];
      const userIdx = participants.findIndex((p) => p.type === "user");
      if (personaId) {
        if (userIdx >= 0) {
          participants[userIdx] = { ...participants[userIdx]!, personaId: personaId as Persona["id"] };
        } else {
          participants.push({
            id: crypto.randomUUID() as Chat["participants"][number]["id"],
            type: "user",
            personaId: personaId as Persona["id"],
          });
        }
      }

      const next: Chat = {
        ...chat,
        title: title.trim() || chat.title,
        participants,
        lorebookIds: lorebookIds as Chat["lorebookIds"],
        authorNoteDepth,
      };
      if (presetId) next.presetId = presetId;
      else delete next.presetId;
      if (authorNote.trim()) next.authorNote = authorNote;
      else delete next.authorNote;

      // 프로바이더 override는 extensions에 보관(스키마 변경 없이, 결정 #7).
      const ext = { ...(chat.extensions ?? {}) } as Record<string, unknown>;
      if (providerId) ext["providerProfileId"] = providerId;
      else delete ext["providerProfileId"];
      next.extensions = ext;

      await storage.chats.put(next);
      await onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const toggleLorebook = (id: string) => {
    setLorebookIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  };

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="flex max-h-[calc(100vh-2rem)] max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col rounded-xl border border-line bg-bg-soft"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <span className="text-sm font-semibold text-ink">{t("chat.settings")}</span>
          <button className="text-ink-faint hover:text-ink" onClick={onClose} aria-label={t("chat.close")}>
            ✕
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 scroll-touch">
          <Field label={t("chatSet.fieldTitle")}>
            <input
              className="w-full rounded-lg border border-line bg-bg-raised px-3 py-2 text-sm text-ink outline-none focus:border-accent"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </Field>

          <Field label={t("chatSet.persona")}>
            <select
              className="w-full rounded-lg border border-line bg-bg-raised px-3 py-2 text-sm text-ink"
              value={personaId ?? ""}
              onChange={(e) => setPersonaId(e.target.value ? (e.target.value as Persona["id"]) : undefined)}
            >
              <option value="">{t("chatSet.defaultPersona")}</option>
              {personas.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("chatSet.preset")} hint={t("chatSet.emptyGlobalHint")}>
            <select
              className="w-full rounded-lg border border-line bg-bg-raised px-3 py-2 text-sm text-ink"
              value={presetId ?? ""}
              onChange={(e) => setPresetId(e.target.value ? (e.target.value as Preset["id"]) : undefined)}
            >
              <option value="">{t("chatSet.globalDefault")}</option>
              {presets.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("chatSet.provider")} hint={t("chatSet.emptyGlobalHint")}>
            <select
              className="w-full rounded-lg border border-line bg-bg-raised px-3 py-2 text-sm text-ink"
              value={providerId ?? ""}
              onChange={(e) => setProviderId(e.target.value || undefined)}
            >
              <option value="">{t("chatSet.globalDefault")}</option>
              {providers.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("chatSet.authorNote")} hint={t("chatSet.authorNoteHint")}>
            <textarea
              className="w-full resize-y rounded-lg border border-line bg-bg-raised px-3 py-2 text-sm text-ink outline-none focus:border-accent"
              rows={3}
              value={authorNote}
              onChange={(event) => setAuthorNote(event.target.value)}
            />
          </Field>

          <Field label={t("chatSet.authorNoteDepth", { depth: authorNoteDepth })} hint={t("chatSet.authorNoteDepthHint")}>
            <input
              type="range"
              min={0}
              max={20}
              value={authorNoteDepth}
              onChange={(e) => setAuthorNoteDepth(Number(e.target.value))}
              className="w-full"
            />
          </Field>

          <Field label={t("chatSet.chatLorebooks")} hint={t("chatSet.chatLorebooksHint")}>
            {lorebooks.length === 0 ? (
              <p className="text-xs text-ink-faint">{t("chatSet.noLorebooks")}</p>
            ) : (
              <ul className="space-y-1">
                {lorebooks.map((b) => (
                  <li key={b.id}>
                    <label className="flex items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        checked={lorebookIds.includes(b.id)}
                        onChange={() => toggleLorebook(b.id)}
                      />
                      {b.name}
                      <span className="text-xs text-ink-faint">({b.entries.length})</span>
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </Field>
        </div>

        <div className="flex justify-end gap-2 border-t border-line px-4 py-3">
          <button
            className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={onClose}
            disabled={busy}
          >
            {t("common.cancel")}
          </button>
          <button
            className="rounded-lg bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
            onClick={save}
            disabled={busy}
          >
            {busy ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-ink-soft">
        {label}
        {hint && <span className="ml-1 text-ink-faint">· {hint}</span>}
      </span>
      {children}
    </label>
  );
}
